import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Banknote,
  CalendarDays,
  ExternalLink,
  FilePenLine,
  FileText,
  Gavel,
  Loader2,
  Pencil,
  Plus,
  Scale,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import { apiService } from '../services/apiService';
import {
  LegalAdministrativeSubtype,
  LegalMatter,
  LegalMatterCategory,
  LegalMatterPriority,
  LegalMatterStatus,
  Tender,
} from '../types';
import { useModal } from './ModalProvider';

const CATEGORIES: Array<{ value: LegalMatterCategory; label: string; icon: React.ComponentType<{ className?: string }> }> = [
  { value: 'NOTIFICATIONS', label: 'Notificações e ofícios', icon: FileText },
  { value: 'COLLECTION', label: 'Processos de cobrança', icon: Banknote },
  { value: 'ADMINISTRATIVE', label: 'Processos administrativos', icon: Gavel },
];

const ADMIN_SUBTYPES: Array<{ value: LegalAdministrativeSubtype; label: string }> = [
  { value: 'APPEAL', label: 'Recurso' },
  { value: 'COUNTERARGUMENTS', label: 'Contrarrazões' },
  { value: 'CHALLENGE', label: 'Impugnação' },
  { value: 'WRIT_OF_MANDAMUS', label: 'Mandado de segurança' },
];

const STATUS_LABELS: Record<LegalMatterStatus, string> = {
  OPEN: 'Aberto', IN_PROGRESS: 'Em andamento', AWAITING: 'Aguardando', COMPLETED: 'Concluído', CANCELLED: 'Cancelado',
};
const PRIORITY_LABELS: Record<LegalMatterPriority, string> = {
  LOW: 'Baixa', NORMAL: 'Normal', HIGH: 'Alta', URGENT: 'Urgente',
};

type LegalForm = Partial<LegalMatter> & {
  category: LegalMatterCategory;
  subject: string;
  openedAt: string;
  status: LegalMatterStatus;
  priority: LegalMatterPriority;
};

const makeEmptyForm = (category: LegalMatterCategory): LegalForm => ({
  category,
  subtype: category === 'ADMINISTRATIVE' ? 'APPEAL' : null,
  subject: '',
  number: '',
  authority: '',
  opposingParty: '',
  status: 'OPEN',
  priority: 'NORMAL',
  openedAt: new Date().toISOString().slice(0, 10),
  deadline: '',
  responsible: '',
  amount: null,
  description: '',
  attachmentName: '',
  attachmentUrl: '',
  tenderId: '',
});

const dateOnly = (value?: string | null) => value ? String(value).slice(0, 10) : '';
const localCalendarDate = (value?: string | null) => {
  if (!value) return null;
  const [year, month, day] = dateOnly(value).split('-').map(Number);
  return year && month && day ? new Date(year, month - 1, day) : null;
};
const formatDate = (value?: string | null) => {
  const date = localCalendarDate(value);
  return date ? new Intl.DateTimeFormat('pt-BR').format(date) : 'Sem prazo';
};
const formatMoney = (value?: number | null) => value == null ? null : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const LegalManager: React.FC = () => {
  const [matters, setMatters] = useState<LegalMatter[]>([]);
  const [tenders, setTenders] = useState<Tender[]>([]);
  const [activeCategory, setActiveCategory] = useState<LegalMatterCategory>('NOTIFICATIONS');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | LegalMatterStatus>('ALL');
  const [subtypeFilter, setSubtypeFilter] = useState<'ALL' | LegalAdministrativeSubtype>('ALL');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<LegalMatter | null>(null);
  const [form, setForm] = useState<LegalForm>(makeEmptyForm('NOTIFICATIONS'));
  const { showAlert, showConfirm } = useModal();

  const load = async () => {
    setLoading(true);
    try {
      const [legalRows, tenderRows] = await Promise.all([apiService.getLegalMatters(), apiService.getTenders()]);
      setMatters(legalRows);
      setTenders(tenderRows);
    } catch (error: any) {
      await showAlert(error?.message || 'Não foi possível carregar o módulo jurídico.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const counts = useMemo(() => CATEGORIES.reduce((acc, item) => {
    acc[item.value] = matters.filter(matter => matter.category === item.value).length;
    return acc;
  }, {} as Record<LegalMatterCategory, number>), [matters]);

  const filtered = useMemo(() => matters.filter(matter => {
    if (matter.category !== activeCategory) return false;
    if (statusFilter !== 'ALL' && matter.status !== statusFilter) return false;
    if (activeCategory === 'ADMINISTRATIVE' && subtypeFilter !== 'ALL' && matter.subtype !== subtypeFilter) return false;
    const needle = search.trim().toLocaleLowerCase('pt-BR');
    if (!needle) return true;
    return [matter.subject, matter.number, matter.authority, matter.opposingParty, matter.responsible, matter.tender?.title]
      .some(value => String(value || '').toLocaleLowerCase('pt-BR').includes(needle));
  }), [activeCategory, matters, search, statusFilter, subtypeFilter]);

  const openCreate = () => {
    setEditing(null);
    setForm(makeEmptyForm(activeCategory));
    setFormOpen(true);
  };

  const openEdit = (matter: LegalMatter) => {
    setEditing(matter);
    setForm({ ...matter, openedAt: dateOnly(matter.openedAt), deadline: dateOnly(matter.deadline), amount: matter.amount ?? null });
    setFormOpen(true);
  };

  const save = async () => {
    if (!form.subject.trim()) return showAlert('Informe o assunto do registro jurídico.');
    if (form.category === 'ADMINISTRATIVE' && !form.subtype) return showAlert('Selecione o tipo do processo administrativo.');
    setSaving(true);
    try {
      const payload = {
        ...form,
        tenderId: form.tenderId || null,
        deadline: form.deadline || null,
        amount: form.category === 'COLLECTION' && form.amount !== null && form.amount !== undefined
          ? Number(form.amount) : null,
      };
      if (editing) await apiService.updateLegalMatter(editing.id, payload);
      else await apiService.createLegalMatter(payload);
      setFormOpen(false);
      await load();
    } catch (error: any) {
      await showAlert(error?.message || 'Não foi possível salvar o registro jurídico.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (matter: LegalMatter) => {
    const confirmed = await showConfirm(`Excluir o registro “${matter.subject}”? Esta ação não poderá ser desfeita.`);
    if (!confirmed) return;
    try {
      await apiService.deleteLegalMatter(matter.id);
      setMatters(current => current.filter(item => item.id !== matter.id));
    } catch (error: any) {
      await showAlert(error?.message || 'Não foi possível excluir o registro jurídico.');
    }
  };

  const subtypeLabel = (value?: string | null) => ADMIN_SUBTYPES.find(item => item.value === value)?.label;

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-[1500px] mx-auto">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-ink-title dark:text-ink-title-dark flex items-center gap-2"><Scale className="w-5 h-5" /> Jurídico</h1>
          <p className="text-sm text-ink-subtitle dark:text-ink-subtitle-dark mt-1">Controle de comunicações, cobranças e medidas administrativas.</p>
        </div>
        <button onClick={openCreate} className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium transition-colors">
          <Plus className="w-4 h-4" /> Novo registro
        </button>
      </div>

      <div className="border-b border-line dark:border-line-dark overflow-x-auto">
        <div className="flex min-w-max gap-1" role="tablist" aria-label="Áreas do jurídico">
          {CATEGORIES.map(item => {
            const Icon = item.icon;
            const active = activeCategory === item.value;
            return (
              <button key={item.value} role="tab" aria-selected={active} onClick={() => { setActiveCategory(item.value); setSubtypeFilter('ALL'); }}
                className={`h-11 px-4 flex items-center gap-2 border-b-2 text-sm font-medium ${active ? 'border-brand-500 text-brand-700 dark:text-brand-300' : 'border-transparent text-ink-subtitle dark:text-ink-subtitle-dark hover:text-ink-title dark:hover:text-ink-title-dark'}`}>
                <Icon className="w-4 h-4" /> {item.label}
                <span className="min-w-6 h-6 px-1.5 grid place-items-center rounded bg-surface-alt dark:bg-surface-dark-alt text-xs">{counts[item.value]}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-3">
        <label className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-subtitle" />
          <input value={search} onChange={event => setSearch(event.target.value)} placeholder="Buscar por assunto, número, órgão ou responsável"
            className="w-full h-10 pl-9 pr-3 rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark text-sm" />
        </label>
        {activeCategory === 'ADMINISTRATIVE' && (
          <select value={subtypeFilter} onChange={event => setSubtypeFilter(event.target.value as any)} className="h-10 px-3 rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark text-sm">
            <option value="ALL">Todos os tipos</option>
            {ADMIN_SUBTYPES.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
        )}
        <select value={statusFilter} onChange={event => setStatusFilter(event.target.value as any)} className="h-10 px-3 rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark text-sm">
          <option value="ALL">Todas as situações</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="min-h-52 grid place-items-center text-ink-subtitle"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="min-h-52 grid place-items-center border-y border-line dark:border-line-dark text-center">
          <div><FilePenLine className="w-8 h-8 mx-auto mb-2 text-ink-subtitle" /><p className="font-medium">Nenhum registro nesta área</p><p className="text-sm text-ink-subtitle mt-1">Use “Novo registro” para iniciar o acompanhamento.</p></div>
        </div>
      ) : (
        <div className="grid gap-3">
          {filtered.map(matter => {
            const deadlineDate = localCalendarDate(matter.deadline);
            if (deadlineDate) deadlineDate.setHours(23, 59, 59, 999);
            const overdue = deadlineDate && deadlineDate.getTime() < Date.now() && !['COMPLETED', 'CANCELLED'].includes(matter.status);
            return (
              <article key={matter.id} className="rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark p-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 text-xs mb-2">
                      {matter.subtype && <span className="px-2 py-1 rounded bg-brand-50 dark:bg-brand-950 text-brand-700 dark:text-brand-300 font-medium">{subtypeLabel(matter.subtype)}</span>}
                      <span className="px-2 py-1 rounded bg-surface-alt dark:bg-surface-dark-alt">{STATUS_LABELS[matter.status]}</span>
                      <span className={`px-2 py-1 rounded font-medium ${matter.priority === 'URGENT' ? 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300' : 'bg-surface-alt dark:bg-surface-dark-alt'}`}>{PRIORITY_LABELS[matter.priority]}</span>
                      {overdue && <span className="px-2 py-1 rounded bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Prazo vencido</span>}
                    </div>
                    <h2 className="font-semibold text-ink-title dark:text-ink-title-dark break-words">{matter.subject}</h2>
                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-ink-subtitle dark:text-ink-subtitle-dark">
                      {matter.number && <span>Nº {matter.number}</span>}
                      {matter.authority && <span>{matter.authority}</span>}
                      {matter.opposingParty && <span>Parte: {matter.opposingParty}</span>}
                      {formatMoney(matter.amount) && <span>{formatMoney(matter.amount)}</span>}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
                      <span className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> Abertura: {formatDate(matter.openedAt)}</span>
                      <span className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> Prazo: {formatDate(matter.deadline)}</span>
                      {matter.responsible && <span>Responsável: {matter.responsible}</span>}
                      {matter.tender && <span>Licitação: {matter.tender.title}</span>}
                    </div>
                    {matter.description && <p className="mt-3 text-sm whitespace-pre-wrap text-ink-body dark:text-ink-body-dark">{matter.description}</p>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {matter.attachmentUrl && <a href={matter.attachmentUrl} target="_blank" rel="noreferrer" title="Abrir documento" className="w-9 h-9 grid place-items-center rounded hover:bg-surface-alt dark:hover:bg-surface-dark-alt"><ExternalLink className="w-4 h-4" /></a>}
                    <button onClick={() => openEdit(matter)} title="Editar" className="w-9 h-9 grid place-items-center rounded hover:bg-surface-alt dark:hover:bg-surface-dark-alt"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => void remove(matter)} title="Excluir" className="w-9 h-9 grid place-items-center rounded text-red-600 hover:bg-red-50 dark:hover:bg-red-950"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {formOpen && (
        <div className="fixed inset-0 z-50 bg-black/55 p-3 grid place-items-center" role="dialog" aria-modal="true" aria-label="Registro jurídico">
          <div className="w-full max-w-3xl max-h-[92vh] overflow-auto rounded-lg bg-surface dark:bg-surface-dark border border-line dark:border-line-dark shadow-xl">
            <div className="h-14 px-5 flex items-center justify-between border-b border-line dark:border-line-dark sticky top-0 bg-surface dark:bg-surface-dark z-10">
              <h2 className="font-semibold">{editing ? 'Editar registro jurídico' : 'Novo registro jurídico'}</h2>
              <button onClick={() => setFormOpen(false)} title="Fechar" className="w-9 h-9 grid place-items-center rounded hover:bg-surface-alt dark:hover:bg-surface-dark-alt"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="text-sm">Área
                <select value={form.category} onChange={event => setForm(current => ({ ...current, category: event.target.value as LegalMatterCategory, subtype: event.target.value === 'ADMINISTRATIVE' ? current.subtype || 'APPEAL' : null }))} className="mt-1 w-full h-10 px-3 rounded-lg border border-line dark:border-line-dark bg-bg dark:bg-bg-dark">
                  {CATEGORIES.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
                </select>
              </label>
              {form.category === 'ADMINISTRATIVE' && <label className="text-sm">Tipo administrativo
                <select value={form.subtype || 'APPEAL'} onChange={event => setForm(current => ({ ...current, subtype: event.target.value as LegalAdministrativeSubtype }))} className="mt-1 w-full h-10 px-3 rounded-lg border border-line dark:border-line-dark bg-bg dark:bg-bg-dark">
                  {ADMIN_SUBTYPES.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
                </select>
              </label>}
              <label className="text-sm md:col-span-2">Assunto *
                <input value={form.subject} onChange={event => setForm(current => ({ ...current, subject: event.target.value }))} className="mt-1 w-full h-10 px-3 rounded-lg border border-line dark:border-line-dark bg-bg dark:bg-bg-dark" />
              </label>
              <label className="text-sm">Número do processo/documento
                <input value={form.number || ''} onChange={event => setForm(current => ({ ...current, number: event.target.value }))} className="mt-1 w-full h-10 px-3 rounded-lg border border-line dark:border-line-dark bg-bg dark:bg-bg-dark" />
              </label>
              <label className="text-sm">Órgão ou autoridade
                <input value={form.authority || ''} onChange={event => setForm(current => ({ ...current, authority: event.target.value }))} className="mt-1 w-full h-10 px-3 rounded-lg border border-line dark:border-line-dark bg-bg dark:bg-bg-dark" />
              </label>
              <label className="text-sm">Parte contrária/devedor
                <input value={form.opposingParty || ''} onChange={event => setForm(current => ({ ...current, opposingParty: event.target.value }))} className="mt-1 w-full h-10 px-3 rounded-lg border border-line dark:border-line-dark bg-bg dark:bg-bg-dark" />
              </label>
              <label className="text-sm">Responsável
                <input value={form.responsible || ''} onChange={event => setForm(current => ({ ...current, responsible: event.target.value }))} className="mt-1 w-full h-10 px-3 rounded-lg border border-line dark:border-line-dark bg-bg dark:bg-bg-dark" />
              </label>
              <label className="text-sm">Situação
                <select value={form.status} onChange={event => setForm(current => ({ ...current, status: event.target.value as LegalMatterStatus }))} className="mt-1 w-full h-10 px-3 rounded-lg border border-line dark:border-line-dark bg-bg dark:bg-bg-dark">
                  {Object.entries(STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </label>
              <label className="text-sm">Prioridade
                <select value={form.priority} onChange={event => setForm(current => ({ ...current, priority: event.target.value as LegalMatterPriority }))} className="mt-1 w-full h-10 px-3 rounded-lg border border-line dark:border-line-dark bg-bg dark:bg-bg-dark">
                  {Object.entries(PRIORITY_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </label>
              <label className="text-sm">Data de abertura
                <input type="date" value={form.openedAt} onChange={event => setForm(current => ({ ...current, openedAt: event.target.value }))} className="mt-1 w-full h-10 px-3 rounded-lg border border-line dark:border-line-dark bg-bg dark:bg-bg-dark" />
              </label>
              <label className="text-sm">Prazo
                <input type="date" value={String(form.deadline || '')} onChange={event => setForm(current => ({ ...current, deadline: event.target.value }))} className="mt-1 w-full h-10 px-3 rounded-lg border border-line dark:border-line-dark bg-bg dark:bg-bg-dark" />
              </label>
              {form.category === 'COLLECTION' && <label className="text-sm">Valor da cobrança
                <input type="number" min="0" step="0.01" value={form.amount ?? ''} onChange={event => setForm(current => ({ ...current, amount: event.target.value === '' ? null : Number(event.target.value) }))} className="mt-1 w-full h-10 px-3 rounded-lg border border-line dark:border-line-dark bg-bg dark:bg-bg-dark" />
              </label>}
              <label className="text-sm md:col-span-2">Licitação vinculada
                <select value={form.tenderId || ''} onChange={event => setForm(current => ({ ...current, tenderId: event.target.value }))} className="mt-1 w-full h-10 px-3 rounded-lg border border-line dark:border-line-dark bg-bg dark:bg-bg-dark">
                  <option value="">Sem vínculo</option>
                  {tenders.map(tender => <option key={tender.id} value={tender.id}>{tender.title} — {tender.organ}</option>)}
                </select>
              </label>
              <label className="text-sm">Nome do anexo
                <input value={form.attachmentName || ''} onChange={event => setForm(current => ({ ...current, attachmentName: event.target.value }))} className="mt-1 w-full h-10 px-3 rounded-lg border border-line dark:border-line-dark bg-bg dark:bg-bg-dark" />
              </label>
              <label className="text-sm">Link do anexo
                <input type="url" value={form.attachmentUrl || ''} onChange={event => setForm(current => ({ ...current, attachmentUrl: event.target.value }))} className="mt-1 w-full h-10 px-3 rounded-lg border border-line dark:border-line-dark bg-bg dark:bg-bg-dark" />
              </label>
              <label className="text-sm md:col-span-2">Descrição e providências
                <textarea rows={4} value={form.description || ''} onChange={event => setForm(current => ({ ...current, description: event.target.value }))} className="mt-1 w-full px-3 py-2 rounded-lg border border-line dark:border-line-dark bg-bg dark:bg-bg-dark resize-y" />
              </label>
            </div>
            <div className="px-5 py-4 flex justify-end gap-2 border-t border-line dark:border-line-dark">
              <button onClick={() => setFormOpen(false)} className="px-4 py-2 rounded-lg border border-line dark:border-line-dark text-sm">Cancelar</button>
              <button onClick={() => void save()} disabled={saving} className="px-4 py-2 rounded-lg bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium disabled:opacity-60 flex items-center gap-2 transition-colors">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LegalManager;
